import React, { useState } from 'react'
import {
  Box, Checkbox, Chip, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { usePatchBrandMutation } from '../../../redux/growthosBrandsApiSlice'
import MappingEditor from './MappingEditor'
import NewMappingEditor from './NewMappingEditor'

const SEGMENTS = ['SME', 'ENTERPRISE']

const BORDER = '#e6e9f2'
const INK_500 = '#64748b'
const INK_700 = '#334155'
const POSITIVE = '#059669'
const NEGATIVE = '#e11d48'

const fmtMoney = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`)
const fmtRange = (from, to) => (!from && !to ? '—' : `${from || '—'} → ${to || '—'}`)

// Real-app column min-widths (px) — kept in sync with the source page's Tailwind min-w
// utilities so the table always horizontal-scrolls the same way, never compresses.
const COL_WIDTH = {
  brand: 220, segment: 180, deals: 530, active: 250, poc: 240, kam: 200, maxSow: 170, revenue: 180,
  excluded: 120, hidden: 170,
}

const headCellSx = {
  px: 2, py: 1.5, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em',
  color: INK_500, borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap',
}
const bodyCellSx = { px: 2, py: 1.5, color: INK_700, borderBottom: `1px solid ${BORDER}` }

/** Shared row/column renderer used by both "Mapping issues" and "Mapped to HubSpot" tables on
 * BrandsPage — same component, two instances, exactly matching the real app's structure. */
export default function BrandTable({ title, description, brands, peopleOptions }) {
  if (!brands?.length) return null
  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 3, borderRadius: '16px', borderColor: BORDER, overflow: 'hidden',
        background: 'linear-gradient(160deg, #ffffff 0%, #fbfbfe 100%)',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 2.5, pt: 2.5, pb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: INK_500 }}>
            {title}
          </Typography>
          {description && (
            <Typography sx={{ fontSize: 12, color: INK_500, mt: 0.5, maxWidth: 900 }}>{description}</Typography>
          )}
        </Box>
        <Chip
          size="small"
          label={`${brands.length} brand${brands.length === 1 ? '' : 's'}`}
          sx={{ borderRadius: '999px', border: `1px solid ${BORDER}`, bgcolor: '#fff', color: INK_700, fontSize: 12, fontWeight: 500 }}
        />
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 2300 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, minWidth: COL_WIDTH.brand }}>Brand</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: COL_WIDTH.segment }}>Segment</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: COL_WIDTH.deals }}>Deal IDs by ProShip tab</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: COL_WIDTH.active }}>Active data</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: COL_WIDTH.poc }}>Sales POC</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: COL_WIDTH.kam }}>KAM</TableCell>
              <Tooltip title="Max share of wallet — the brand's shipment ceiling. HubSpot fallback + KAM/SAM manual override.">
                <TableCell sx={{ ...headCellSx, minWidth: COL_WIDTH.maxSow, cursor: 'help' }}>Max SOW</TableCell>
              </Tooltip>
              <TableCell sx={{ ...headCellSx, minWidth: COL_WIDTH.revenue }}>Revenue (all time)</TableCell>
              <TableCell align="center" sx={{ ...headCellSx, minWidth: COL_WIDTH.excluded }}>Excluded</TableCell>
              <TableCell align="center" sx={{ ...headCellSx, minWidth: COL_WIDTH.hidden }}>Hide from display</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {brands.map((b) => <BrandRow key={b.id} brand={b} peopleOptions={peopleOptions} />)}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

function BrandRow({ brand, peopleOptions }) {
  const { enqueueSnackbar } = useSnackbar()
  const [patchBrand] = usePatchBrandMutation()
  const [maxSow, setMaxSow] = useState(brand.maxSowManual ?? '')

  const patch = async (body, successMsg) => {
    try {
      await patchBrand({ id: brand.id, body }).unwrap()
      if (successMsg) enqueueSnackbar(successMsg, { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Update failed.', { variant: 'error' })
    }
  }

  const mappings = brand.mappings || []

  return (
    <TableRow
      sx={{
        '&:hover': {
          background: 'linear-gradient(90deg, rgba(79,70,229,0.06), rgba(6,182,212,0.04))',
          boxShadow: 'inset 3px 0 0 #4f46e5',
        },
      }}
    >
      <TableCell sx={bodyCellSx}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK_700 }}>{brand.brandName}</Typography>
        <Typography sx={{ fontSize: 11, color: INK_500 }}>
          {[brand.vertical, brand.city].filter(Boolean).join(' · ') || '—'}
        </Typography>
      </TableCell>

      <TableCell sx={bodyCellSx}>
        <Select size="small" value={brand.segment || ''} onChange={(e) => patch({ segment: e.target.value })} displayEmpty sx={{ fontSize: 12, minWidth: 110 }}>
          <MenuItem value=""><em>—</em></MenuItem>
          {SEGMENTS.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>)}
        </Select>
      </TableCell>

      <TableCell sx={bodyCellSx}>
        {mappings.length
          ? (
            <Box sx={mappings.length > 1 ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 } : { display: 'flex', flexDirection: 'column', gap: 1 }}>
              {mappings.map((m) => <MappingEditor key={m.sourceDealId} brandId={brand.id} mapping={m} />)}
            </Box>
          )
          : <NewMappingEditor brandId={brand.id} />}
      </TableCell>

      <TableCell sx={bodyCellSx}>
        <Typography sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtRange(brand.firstShipmentMonth, brand.lastShipmentMonth)}</Typography>
      </TableCell>

      <TableCell sx={bodyCellSx}>
        {/* Placed here, not on the Brand cell — a POC mismatch is a raw-source-value-vs-HubSpot-
            owner problem, so it belongs next to the field it's actually about. */}
        {brand.pocMismatches?.map((m, i) => (
          <Box key={i} sx={{
            display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5, px: 1, py: 0.5,
            border: '1px solid', borderColor: 'rgba(225,29,72,.32)', borderRadius: '8px',
          }}>
            <WarningAmberIcon sx={{ fontSize: 11, mt: '1px', color: NEGATIVE, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 10, color: INK_500 }}>
              Sheet ({m.sourceTabs?.join(' + ') || '—'}) says <Box component="span" sx={{ fontFamily: 'monospace' }}>&quot;{m.rawPoc}&quot;</Box> — not found in HubSpot. Confirm the correct person below.
            </Typography>
          </Box>
        ))}
        {!brand.pocMismatches?.length && brand.salesPocId && (
          <Tooltip title="This brand's ProShip sheet Sales POC text exact-matched a HubSpot owner — not a guess.">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, width: 'fit-content' }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 11, color: POSITIVE }} />
              <Typography sx={{ fontSize: 10, fontWeight: 600, color: POSITIVE }}>HubSpot = ProShip match</Typography>
            </Box>
          </Tooltip>
        )}
        <Select
          size="small"
          value={brand.salesPocId || ''}
          onChange={(e) => patch({ salesPocId: e.target.value || null })}
          displayEmpty
          sx={{ fontSize: 12, minWidth: 160 }}
        >
          <MenuItem value=""><em>Unassigned</em></MenuItem>
          {peopleOptions?.map((p) => (
            <MenuItem key={p.hubspotOwnerId} value={p.hubspotOwnerId} sx={{ fontSize: 12 }}>
              {p.fullName}{p.isActive === false ? ' (inactive)' : ''}
            </MenuItem>
          ))}
        </Select>
      </TableCell>

      <TableCell sx={bodyCellSx}>
        <Tooltip title="From the merchant's Account Team tab (Merchants → view → Account Team). Change it there.">
          <Typography sx={{ fontSize: 12, color: brand.kamName ? INK_700 : INK_500, cursor: 'help' }}>
            {brand.kamName || 'Not mapped'}
          </Typography>
        </Tooltip>
      </TableCell>

      <TableCell sx={bodyCellSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Tooltip title="HubSpot's computed max orders/month across this brand's mapped deals — a fallback used only when no manual override is set.">
            <Typography sx={{ fontSize: 11, color: 'text.disabled', cursor: 'help' }}>
              HubSpot: {brand.maxSowHubspot ?? '—'}
            </Typography>
          </Tooltip>
          <TextField
            size="small"
            type="number"
            value={maxSow}
            placeholder="By KAM/SAM"
            onChange={(e) => setMaxSow(e.target.value)}
            onBlur={() => {
              const v = maxSow === '' ? null : Number(maxSow)
              if (v !== (brand.maxSowManual ?? null)) patch({ maxSowManual: v }, 'Max SOW updated.')
            }}
            sx={{ width: 110, '& input': { fontSize: 12 } }}
          />
        </Box>
      </TableCell>

      <TableCell sx={bodyCellSx}>
        <Typography sx={{ fontSize: 12 }}>{fmtMoney(brand.totalRevenue)}</Typography>
      </TableCell>

      <TableCell align="center" sx={bodyCellSx}>
        {/* Jackson strips the "is" prefix off GrowthOsBrandResponse's primitive-boolean
            getters, so the wire fields are "excluded"/"hiddenFromDisplay" — the PATCH body
            keys stay "isExcluded"/"isHiddenFromDisplay" since patchBrand reads a raw Map. */}
        <Checkbox
          size="small"
          checked={!!brand.excluded}
          onChange={(e) => patch({ isExcluded: e.target.checked })}
          title="Removes this brand from every analytics calculation."
        />
      </TableCell>

      <TableCell align="center" sx={bodyCellSx}>
        <Checkbox
          size="small"
          checked={!!brand.hiddenFromDisplay}
          onChange={(e) => patch({ isHiddenFromDisplay: e.target.checked })}
          title="Hides this brand's identity in analytics tables and charts, while retaining every value in totals and calculations."
        />
      </TableCell>
    </TableRow>
  )
}
